import { TFunction, useT } from '@transifex/react'
import { GiftCard, GiftCardStatus } from '@vend/types'
import { useCallback } from 'react'
import { vn } from 'vend-number'
import { GiftCardModalFlow, GiftCardType } from './modal/GiftCardModal'

/**
 * Get translated message for in store gift card & big commerce gift cert with no balance
 */
const getNoBalanceMessage = (
  t: TFunction,
  giftcardType: string,
  giftcardStatus: GiftCardStatus
) => {
  switch (giftcardStatus) {
    case 'REDEEMED':
      if (giftcardType === 'BIGCOMMERCE') {
        return t(
          `This certificate has been fully redeemed and cannot be used.`,
          {
            _context: 'Big commerce gift cert already redeemed',
          }
        )
      }
      return t(`This card has been fully redeemed and cannot be used.`, {
        _context: 'Gift card already redeemed',
      })
    case 'VOIDED':
      if (giftcardType === 'BIGCOMMERCE') {
        return t(`This certificate has been canceled and cannot be used.`, {
          _context: 'Big commerce gift cert already canceled',
        })
      }
      return t(`This card has been canceled and cannot be used.`, {
        _context: 'Gift card already canceled',
      })
    case 'EXPIRED':
      if (giftcardType === 'BIGCOMMERCE') {
        return t(`This certificate is expired and cannot be used.`, {
          _context: 'Big commerce gift cert already expired',
        })
      }
      return t(`This card is expired and cannot be used.`, {
        _context: 'Gift card already expired',
      })
    default:
      if (giftcardType === 'BIGCOMMERCE') {
        return t(`This certificate has no funds and cannot be used.`, {
          _context: 'Big commerce gift cert has no funds',
        })
      }
      return t(`This card has no funds and cannot be used.`, {
        _context: 'Gift card has no funds',
      })
  }
}

/**
 * Get translated message for in store gift card & big commerce gift cert with balance insufficient
 */
const getBalanceNotEnoughMessage = (
  t: TFunction,
  giftCardType: GiftCardType
) => {
  if (giftCardType === 'BIGCOMMERCE') {
    return t(
      `This certificate has insufficient funds to complete the sale. You may still use this gift certificate to make a partial payment.`,
      {
        _context: `Big commerce gift cert balance in sufficient`,
      }
    )
  }
  return t(
    `This card has insufficient funds to complete the sale. You may still use this gift card to make a partial payment.`,
    {
      _context: `Gift card balance in sufficient`,
    }
  )
}

export const useRedeemGiftCard = () => {
  const t = useT()

  const isBalanceSufficient = useCallback(
    (giftcard: GiftCard, amountTendered: number) => {
      return vn(giftcard.balance).gte(amountTendered)
    },
    []
  )
  const getPayableAmount = useCallback(
    (giftcard: GiftCard, amountTendered: number) => {
      const isSufficient = isBalanceSufficient(giftcard, amountTendered)
      return isSufficient ? amountTendered : giftcard.balance
    },
    [isBalanceSufficient]
  )

  const getRedeemMessages = useCallback(
    (
      giftcard: GiftCard,
      giftCardType: GiftCardType,
      amountTendered: string,
      flow: GiftCardModalFlow
    ) => {
      let noBalanceMsg = ''
      let balanceNotEnoughMsg = ''

      if (!amountTendered || isNaN(Number(amountTendered))) {
        return { noBalanceMsg, balanceNotEnoughMsg }
      }
      if (flow === 'SELLING') {
        return { noBalanceMsg, balanceNotEnoughMsg }
      }
      const hasBalance = vn(giftcard.balance).gt(0)
      const hasEnoughBalance = isBalanceSufficient(
        giftcard,
        Number(amountTendered)
      )

      if (!hasBalance) {
        noBalanceMsg = getNoBalanceMessage(t, giftCardType, giftcard.status)
      } else if (!hasEnoughBalance) {
        balanceNotEnoughMsg = getBalanceNotEnoughMessage(t, giftCardType)
      }
      return { noBalanceMsg, balanceNotEnoughMsg }
    },
    [t, isBalanceSufficient]
  )

  return {
    getPayableAmount,
    isBalanceSufficient,
    getRedeemMessages,
  }
}
